import { useEffect, useState, useCallback } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import Image from "next/image";
import { IoMdClose } from "react-icons/io";

const SCROLL_THRESHOLD = 0.55;
const TIME_DELAY_MS = 15000;
const STORAGE_KEY = "certificate_popup_dismissed";

export default function CertificatePopup() {
    const router = useRouter();
    const { t } = useTranslation("common");
    const [isOpen, setIsOpen] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const isHomePage = router.pathname === "/";

    useEffect(() => {
        if (sessionStorage.getItem(STORAGE_KEY)) {
            setDismissed(true);
        }
    }, []);

    const showPopup = useCallback(() => {
        if (!sessionStorage.getItem(STORAGE_KEY)) {
            setIsOpen(true);
        }
    }, []);

    useEffect(() => {
        if (!isHomePage || dismissed) return;

        const timeTimer = setTimeout(showPopup, TIME_DELAY_MS);

        const onScroll = () => {
            const scrollTop = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (docHeight > 0 && scrollTop / docHeight >= SCROLL_THRESHOLD) {
                showPopup();
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });

        const isDesktop = () => window.innerWidth >= 1024;
        const onMouseLeave = (e) => {
            if (!isDesktop()) return;
            if (e.clientY <= 0) showPopup();
        };
        document.documentElement.addEventListener("mouseleave", onMouseLeave);

        return () => {
            clearTimeout(timeTimer);
            window.removeEventListener("scroll", onScroll);
            document.documentElement.removeEventListener("mouseleave", onMouseLeave);
        };
    }, [isHomePage, dismissed, showPopup]);

    const handleClose = () => {
        setIsOpen(false);
        setDismissed(true);
        sessionStorage.setItem(STORAGE_KEY, "1");
    };

    if (!isHomePage || !isOpen || dismissed) return null;

    return (
        <>
            <div
                className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm"
                aria-hidden="true"
                onClick={handleClose}
            />
            <div
                className="fixed left-1/2 top-1/2 z-[9999] w-[90vw] max-w-[650px] h-[500px] sm:h-[650px] bg-white -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[24px] shadow-2xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="certificate-popup-title"
            >
                <div
                    className="relative flex items-center justify-center px-6 py-10 h-[225px]"
                    style={{
                        backgroundImage: "url(/images/popup-bg.svg)",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute right-3 top-3 z-10 flex sm:h-[36px] sm:w-[36px] h-[24px] w-[24px] items-center justify-center rounded-full border border-gray-300 bg-[#FFFFFFE5] text-[#374151] transition cursor-pointer"
                        aria-label="Close"
                    >
                        <IoMdClose className="text-[20px] sm:text-[24px]" />
                    </button>
                </div>
                <div className="bg-white px-4 sm:px-6 pb-8 pt-6">
                    <div className="-mt-[200px] sm:-mt-[220px] relative z-10 max-w-[230px] sm:max-w-[440px] mx-auto" >
                        <Image src="/images/certificate.png" alt="Certificate" width={220} height={160} className="h-auto w-[160px] object-contain sm:w-[260px] mx-auto" />
                    </div>
                    <h2
                        id="certificate-popup-title"
                        className="text-center mt-[24px] text-[24px] font-[600] bg-gradient-to-b from-[#091F59] to-[#1A3884] text-transparent bg-clip-text sm:text-[32px]"
                    >
                        {t("popup.title")}
                    </h2>
                    <p className="mt-[8px] sm:mt-[16px] text-center text-[16px] sm:text-[20px] font-[500] leading-relaxed text-black">
                        {t("popup.description")}
                    </p>
                    <Link
                        href="/apply"
                        onClick={() => { handleClose(); trackApplyButton("popup"); }}
                        className="mt-[16px] sm:mt-[32px] flex w-full sm:w-[220px] h-[44px] sm:h-[48px] mx-auto items-center justify-center rounded-full bg-main-pink text-[14px] sm:text-[16px] font-[500] text-white shadow-md transition hover:opacity-95"
                    >
                        {t("popup.button")}
                    </Link>
                </div>
            </div>
        </>
    );
}
